/**
 * Web Push subscription management for Crays web app.
 * Registers the service worker and manages browser push subscriptions.
 */

import apiClient from '../store/api/apiClient';

const PUSH_API_BASE = '/notifications/push';

/** Convert a URL-safe base64 string to Uint8Array (required by pushManager.subscribe) */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push] Service worker registered');
    return registration;
  } catch (err) {
    console.error('[Push] Service worker registration failed:', err);
    return null;
  }
}

export async function subscribeToPush(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  try {
    // Fetch VAPID public key from backend
    const keyRes = await apiClient.get<{ data?: { publicKey?: string } }>(
      `${PUSH_API_BASE}/vapid-key`,
    );
    const publicKey = keyRes.data.data?.publicKey;
    if (!publicKey) return null;

    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    // apiClient rejects on non-2xx, so a failed registration lands in the catch below.
    await apiClient.post(`${PUSH_API_BASE}/subscribe`, {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
        auth:   btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
      },
      userAgent: navigator.userAgent,
    });

    console.log('[Push] Subscribed to push notifications');
    return subscription;
  } catch (err) {
    console.error('[Push] Failed to subscribe:', err);
    return null;
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    // getRegistration (not .ready) so logout doesn't hang when no worker was ever registered.
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;

    await apiClient.delete(`${PUSH_API_BASE}/unsubscribe`, {
      data: { endpoint: subscription.endpoint },
    });

    await subscription.unsubscribe();
    console.log('[Push] Unsubscribed from push notifications');
  } catch (err) {
    console.error('[Push] Failed to unsubscribe:', err);
  }
}
